import React from 'react';
import { MapPin, Clock, Store, Warehouse, Tent, MessageSquare } from 'lucide-react';

interface AdminDetailsTabProps {
  location: any;
  request: any;
}

const InfoRow = ({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) => {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="py-2.5 border-b border-stone-100 last:border-0">
      <p className="text-[11px] font-medium text-stone-400 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-sm font-medium text-stone-800 ${mono ? 'font-mono' : ''}`}>{String(value)}</p>
    </div>
  );
};

const typeLabel = (t: string) => {
  switch (t) {
    case 'store': return 'Store';
    case 'warehouse': return 'Warehouse';
    case 'popup': return 'Pop-up';
    default: return 'Location';
  }
};

const typeIcon = (t: string) => {
  switch (t) {
    case 'store': return <Store size={14} className="text-stone-500" />;
    case 'warehouse': return <Warehouse size={14} className="text-stone-500" />;
    case 'popup': return <Tent size={14} className="text-stone-500" />;
    default: return <MapPin size={14} className="text-stone-500" />;
  }
};

const AdminDetailsTab: React.FC<AdminDetailsTabProps> = ({ location, request }) => {
  const data = location || request;
  const addr = data?.address;
  const addressStr = addr
    ? `${addr.street}${addr.street2 ? `, ${addr.street2}` : ''}, ${addr.city}, ${addr.state} ${addr.zip}`
    : undefined;

  return (
    <div className="space-y-4">
      {/* Info Request Banner */}
      {request?.infoRequested && request.infoRequestMessage && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <MessageSquare size={18} className="text-blue-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-700">Info requested from tenant:</p>
              <p className="text-sm text-blue-600 mt-1">{request.infoRequestMessage}</p>
              {request.tenantResponse && (
                <div className="mt-3 pt-3 border-t border-blue-200">
                  <p className="text-sm font-medium text-blue-700">Tenant response:</p>
                  <p className="text-sm text-blue-600 mt-1">{request.tenantResponse}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Location Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
          <MapPin size={16} className="text-stone-500" />
          Location Information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          <InfoRow label="Location Name" value={data?.name} />
          <InfoRow label="Location Type" value={data?.type ? typeLabel(data.type) : undefined} />
          <InfoRow label="Address" value={addressStr} />
          <InfoRow label="Phone" value={data?.phone} />
          <InfoRow label="Timezone" value={data?.timezone} />
          <InfoRow label="Status" value={request?.status} />
        </div>
      </div>

      {/* Subscription */}
      {request && (
        <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
          <h3 className="text-sm font-semibold text-stone-800 mb-4 flex items-center gap-2">
            <Clock size={16} className="text-stone-500" />
            Subscription
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <InfoRow label="Monthly Plan" value={request.isFree ? 'Free' : request.priceAmount ? `$${(request.priceAmount / 100).toFixed(2)}/mo` : 'N/A'} />
            <InfoRow label="Request ID" value={request.requestId} mono />
            <InfoRow label="Created" value={request.requestedAt || request.createdAt ? new Date(request.requestedAt || request.createdAt).toLocaleDateString() : undefined} />
            {request.approvedAt && <InfoRow label="Approved" value={new Date(request.approvedAt).toLocaleDateString()} />}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDetailsTab;
